import { jsx } from "react/jsx-runtime";
import { createContext, useState, useLayoutEffect, useEffect, useContext } from "react";
const initialState = {
  theme: "system",
  setTheme: () => null,
  actualTheme: "light"
};
const ThemeProviderContext = createContext(initialState);
function isTheme(value) {
  return value === "light" || value === "dark" || value === "system";
}
function resolveSystemTheme() {
  if (typeof document !== "undefined") {
    const colorScheme = document.documentElement.getAttribute("data-color-scheme");
    if (colorScheme === "dark" || colorScheme === "light") return colorScheme;
    const bootstrapTheme = document.documentElement.getAttribute("data-bs-theme");
    if (bootstrapTheme === "dark" || bootstrapTheme === "light") return bootstrapTheme;
  }
  try {
    return window.matchMedia?.("(prefers-color-scheme: dark)").matches ? "dark" : "light";
  } catch {
    return "light";
  }
}
function resolveTheme(theme) {
  return theme === "system" ? resolveSystemTheme() : theme;
}
function findAppRoot(rootSelector) {
  if (typeof document === "undefined") return null;
  if (rootSelector) {
    try {
      return document.querySelector(rootSelector);
    } catch {
      return null;
    }
  }
  return document.getElementById("easycommerce-admin-root") || document.getElementById("easycommerce-store-root") || document.getElementById("root") || document.querySelector("[data-theme-root]");
}
function applyResolvedTheme(resolvedTheme, rootSelector, applyToDocumentRoot) {
  if (typeof document === "undefined") return;
  const htmlRoot = document.documentElement;
  const appRoot = findAppRoot(rootSelector);
  if (applyToDocumentRoot) {
    htmlRoot.classList.remove("light", "dark");
    htmlRoot.classList.add(resolvedTheme);
    htmlRoot.style.colorScheme = resolvedTheme;
  }
  if (appRoot && appRoot !== htmlRoot) {
    appRoot.classList.remove("light", "dark");
    appRoot.classList.add(resolvedTheme);
    appRoot.dataset.colorScheme = resolvedTheme;
    appRoot.style.colorScheme = resolvedTheme;
  }
}
function ThemeProvider({
  children,
  defaultTheme = "system",
  storageKey = "ui-theme",
  rootSelector,
  applyToDocumentRoot = true,
  ...props
}) {
  const [theme, setThemeState] = useState(() => {
    if (typeof window !== "undefined") {
      try {
        const storedTheme = localStorage.getItem(storageKey);
        return isTheme(storedTheme) ? storedTheme : defaultTheme;
      } catch {
        return defaultTheme;
      }
    }
    return defaultTheme;
  });
  const [actualTheme, setActualTheme] = useState(() => resolveTheme(theme));
  useLayoutEffect(() => {
    const resolvedTheme = resolveTheme(theme);
    applyResolvedTheme(resolvedTheme, rootSelector, applyToDocumentRoot);
    setActualTheme(resolvedTheme);
  }, [applyToDocumentRoot, rootSelector, theme]);
  useEffect(() => {
    if (theme !== "system") return;
    const syncSystemTheme = () => {
      const resolvedTheme = resolveSystemTheme();
      applyResolvedTheme(resolvedTheme, rootSelector, applyToDocumentRoot);
      setActualTheme(resolvedTheme);
    };
    const mediaQuery = window.matchMedia?.("(prefers-color-scheme: dark)");
    mediaQuery?.addEventListener?.("change", syncSystemTheme);
    const observer = typeof MutationObserver !== "undefined" ? new MutationObserver(syncSystemTheme) : null;
    observer?.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-color-scheme", "data-bs-theme"]
    });
    return () => {
      mediaQuery?.removeEventListener?.("change", syncSystemTheme);
      observer?.disconnect();
    };
  }, [applyToDocumentRoot, rootSelector, theme]);
  const value = {
    theme,
    setTheme: (theme2) => {
      try {
        localStorage.setItem(storageKey, theme2);
      } catch {
      }
      setThemeState(theme2);
    },
    actualTheme
  };
  return /* @__PURE__ */ jsx(ThemeProviderContext.Provider, { ...props, value, children });
}
const useTheme = () => {
  const context = useContext(ThemeProviderContext);
  if (context === void 0) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
};
export {
  ThemeProvider,
  useTheme
};
//# sourceMappingURL=theme-context.js.map
